import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  FolderPlus,
  Info,
  Minus,
  Pencil,
  Star,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import type { HistoryItem } from '@translate-ai/shared';
import { formatDurationSeconds } from '@/lib/duration';
import { localizeLabel } from '@/lib/labels';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useUILocale } from '@/hooks/useUILocale';
import { Tooltip } from '@/components/Tooltip';

interface HistoryItemCardProps {
  item: HistoryItem;
  onLoadIntoForm?: () => void;
  onToggleFavorite?: () => void;
  onOpenLists?: () => void;
  onDelete?: () => void;
  onRemoveFromList?: () => void;
  className?: string;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <button
      type="button"
      onClick={() => copy(text)}
      aria-label={label}
      title={label}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
    >
      {copied ? (
        <Check className="h-4 w-4 text-emerald-600" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
}

export function HistoryItemCard({
  item,
  onLoadIntoForm,
  onToggleFavorite,
  onOpenLists,
  onDelete,
  onRemoveFromList,
  className,
}: HistoryItemCardProps) {
  const { t } = useTranslation();
  const locale = useUILocale();
  const [expanded, setExpanded] = useState(false);
  const sourceRef = useRef<HTMLParagraphElement>(null);
  const translatedRef = useRef<HTMLParagraphElement>(null);
  const [canExpand, setCanExpand] = useState(false);

  useLayoutEffect(() => {
    if (expanded) return;
    const src = sourceRef.current;
    const trg = translatedRef.current;
    if (!src || !trg) return;
    const measure = () => {
      setCanExpand(
        src.scrollHeight > src.clientHeight + 1 ||
          trg.scrollHeight > trg.clientHeight + 1,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(src);
    ro.observe(trg);
    return () => ro.disconnect();
  }, [expanded, item.sourceText, item.translatedText]);

  return (
    <div className={clsx('card bg-white p-3', className)}>
      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate">
            {item.sourceLang} → {item.targetLang}
          </span>
          <Tooltip
            side="bottom"
            ariaLabel={t('history.modelInfo')}
            label={
              <span>
                <span className="block">
                  <span className="text-slate-500">{t('history.modelInfoModel')}:</span>{' '}
                  {item.model}
                </span>
                {item.reasoningEffort != null && (
                  <span className="block">
                    <span className="text-slate-500">
                      {t('history.modelInfoReasoning')}:
                    </span>{' '}
                    {t(`admin.models.reasoningEffortOption.${item.reasoningEffort}`)}
                  </span>
                )}
                {item.durationMs != null && (
                  <span className="block">
                    <span className="text-slate-500">
                      {t('history.modelInfoDuration')}:
                    </span>{' '}
                    {t('history.modelInfoDurationValue', {
                      seconds: formatDurationSeconds(item.durationMs),
                    })}
                  </span>
                )}
              </span>
            }
          >
            <button
              type="button"
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-slate-400 transition hover:text-slate-600"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {onLoadIntoForm && (
            <button
              type="button"
              onClick={onLoadIntoForm}
              aria-label={t('translate.useAsInput')}
              title={t('translate.useAsInput')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label={t('lists.deleteTranslation')}
              title={t('lists.deleteTranslation')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {onRemoveFromList && (
            <button
              type="button"
              onClick={onRemoveFromList}
              aria-label={t('lists.removeFromList')}
              title={t('lists.removeFromList')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100"
            >
              <Minus className="h-4 w-4" />
            </button>
          )}
          {onOpenLists && (
            <button
              type="button"
              onClick={onOpenLists}
              aria-label={t('history.lists')}
              title={t('history.lists')}
              className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100"
            >
              <FolderPlus className="h-4 w-4" />
              {item.listCount > 0 && (
                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-none text-white">
                  {item.listCount}
                </span>
              )}
            </button>
          )}
          {onToggleFavorite && (
            <button
              type="button"
              onClick={onToggleFavorite}
              aria-label={item.isFavorite ? t('history.unfavorite') : t('history.favorite')}
              title={item.isFavorite ? t('history.unfavorite') : t('history.favorite')}
              className={clsx(
                'inline-flex h-8 w-8 items-center justify-center rounded-md transition hover:bg-slate-100',
                item.isFavorite ? 'text-amber-500' : 'text-slate-400',
              )}
            >
              <Star className={clsx('h-4 w-4', item.isFavorite && 'fill-current')} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            disabled={!canExpand}
            aria-label={expanded ? t('history.collapse') : t('history.expand')}
            title={expanded ? t('history.collapse') : t('history.expand')}
            aria-expanded={expanded}
            className={clsx(
              'inline-flex h-8 w-8 items-center justify-center rounded-md transition',
              canExpand
                ? 'text-slate-500 hover:bg-slate-100'
                : 'cursor-not-allowed text-slate-300',
            )}
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      {item.enabledSlots.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
          <span>{t('history.optionsLabel')}</span>
          {item.enabledSlots.map((s) => {
            const label = localizeLabel(s.labels, locale);
            if (!label) return null;
            return (
              <span
                key={s.id}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700"
              >
                {label}
              </span>
            );
          })}
        </div>
      )}
      <div className="mt-1 flex items-start gap-2">
        <CopyButton text={item.sourceText} label={t('history.copySource')} />
        <p
          ref={sourceRef}
          className={clsx(
            'flex-1 whitespace-pre-wrap pt-1 text-sm text-slate-900',
            !expanded && 'line-clamp-2',
          )}
        >
          {item.sourceText}
        </p>
      </div>
      <div className="mt-1 flex items-start gap-2">
        <CopyButton text={item.translatedText} label={t('history.copyTranslation')} />
        <p
          ref={translatedRef}
          className={clsx(
            'flex-1 whitespace-pre-wrap pt-1 text-sm text-slate-500',
            !expanded && 'line-clamp-2',
          )}
        >
          {item.translatedText}
        </p>
      </div>
    </div>
  );
}
